import React, { useEffect, useState } from 'react';
import { useNavigation } from 'context/NavigationContext';
import {
    BringzzPageContainer,
    BringzzIcon,
    BringzzBasketProduct,
    BringzzAccordion,
    BringzzButton,
    BringzzText
} from "@bringzz/components";
import { useParams } from 'react-router-dom';
import useRequest from 'hooks/useRequest';
import { useOrder } from 'context/OrderContext';
import BringzzCustomerCardSmall from 'components/BringzzCustomerCardSmall';
import { getOrderTypeString } from 'utils';
import BringzzOrderCardSmallAccordion from 'components/BringzzOrderCardSmallAccordion';
import { USER_ROLES, DELIVERY_TYPES_LABELS, DELIVERY_TYPES, getDeliveryTypeLabel, ORDER_STATUS } from 'utils/constants';
import useUser from 'hooks/useUser';
import BringzzReadOnlyMap from 'components/BringzzReadOnlyMap';
import { useTranslation } from 'react-i18next';
import ImageViewerModal from 'components/ImageViewerModal';

const OrderViewPage = () => {
    const { backPress, navigate } = useNavigation();
    const { uuid } = useParams();
    const { startMatchingProcess } = useOrder()
    const { sendRequest, data, error, loading } = useRequest();
    const [orderDetails, setOrderDetails] = useState()
    const [showPrescriptionModal, setShowPrescriptionModal] = useState(false);
    const { getUser } = useUser()
    const user = getUser()
    const { t } = useTranslation();

    const fetchOrder = async () => {
        await sendRequest({
            method: 'POST',
            url: `/module/order/getOrder`,
            data: {
                "orderUUID": uuid
            }
        }).then((response) => {
            if (response.status === 'success') {
                setOrderDetails(response.data)
                console.log(response);
            }
        }).catch((error) => {
            console.error(error);
        })
    }

    useEffect(() => {
        if (!uuid) {
            backPress()
        }
        else {
            fetchOrder()
        }
    }, [])

    const acceptOrder = async () => {
        if (orderDetails) {
            const data = await sendRequest({
                method: 'POST',
                url: `/module/order/acceptOrder`,
                data: {
                    "orderUUID": uuid
                }
            }).then((response) => {
                if (response.status === 'success') {
                    return response.data
                }
            }).catch((error) => {
                console.error(error);
            })

            if (data) {
                startMatchingProcess(orderDetails, 0)
                if (USER_ROLES.PHARMACY_ROLE === user.userRole) {
                    navigate('/matchmaking/pharmacy/0')
                } else if (USER_ROLES.SHOPPER_ROLE === user.userRole) {
                    navigate('/matchmaking/shopper/0')
                }
            }
        }
    }

    if (!orderDetails) {
        return (
            <BringzzPageContainer
                className="bg-white"
                header={{
                    left: (
                        <BringzzIcon
                            size='22'
                            icon='IconChevronLeft'
                            folder='LineIcons'
                            className='cursor-pointer'
                            onClick={backPress}
                        />
                    ),
                    title: t('component.order_view.loading_order')
                }}
                authenticated={true}
            >
                <div className="text-center py-6">
                    <BringzzText tag="h3">{t('component.order_view.loading')}</BringzzText>
                </div>
            </BringzzPageContainer>
        );
    }

    return (
        <BringzzPageContainer
            className="bg-white"
            header={{
                left: (
                    <BringzzIcon
                        size='22'
                        icon='IconChevronLeft'
                        folder='LineIcons'
                        className='cursor-pointer'
                        onClick={backPress}
                    />
                ),
                title: `Order #${orderDetails.id}`
            }}
            footerHover={orderDetails.deliveryStatus !== ORDER_STATUS.DONE}
            footer={orderDetails.deliveryStatus !== ORDER_STATUS.DONE ? <div className='bg-white'>
                <div className='p-4 justify-center text-center'>
                    <BringzzText tag='h5'>{t('component.order_view.accept_terms')}</BringzzText>
                    <div className='flex space-x-2 justify-center mt-2'>
                        <BringzzButton
                            className="bg-white text-red-400 border-red-400 border-2"
                            onClick={() => navigate('/')}
                            disabled={loading}
                        >
                            {t('component.order_view.decline')}
                        </BringzzButton>
                        <BringzzButton className="bg-magic-lilac"
                            onClick={acceptOrder}
                            disabled={loading}
                        >{t('component.order_view.accept')}</BringzzButton>
                    </div>
                </div>

            </div> : null}
            authenticated={true}
        >
            {orderDetails && <div className='px-6'>
                <div className='text-center my-4'>
                    <BringzzText tag='h1'>€ {(parseFloat(orderDetails.totalPrice) || 0).toFixed(2)}</BringzzText>
                    <BringzzText tag='h3'>{orderDetails.products.length} {t('component.order_view.items')}</BringzzText>
                </div>

                {/* Price Breakdown */}
                {orderDetails.fees && (
                    <div className='bg-gray-50 rounded-lg p-4 mb-4'>
                        <BringzzText tag='h3' className='mb-3'>{t('component.order_view.price_breakdown')}</BringzzText>
                        <div className='space-y-2'>
                            <div className='flex justify-between'>
                                <BringzzText tag='h4' className='text-gray-600'>{t('component.order_view.products')}</BringzzText>
                                <BringzzText tag='h4'>€ {(parseFloat(orderDetails.productsPrice) || 0).toFixed(2)}</BringzzText>
                            </div>
                            {orderDetails.fees.types.bringzzFee && (
                                <div className='flex justify-between'>
                                    <BringzzText tag='h4' className='text-gray-600'>{t('component.order_view.bringzz_fee')}</BringzzText>
                                    <BringzzText tag='h4'>€ {parseFloat(orderDetails.fees.types.bringzzFee).toFixed(2)}</BringzzText>
                                </div>
                            )}
                            {orderDetails.fees.types.bringzzClickCollect && (
                                <div className='flex justify-between'>
                                    <BringzzText tag='h4' className='text-gray-600'>{t('component.order_view.click_collect_fee')}</BringzzText>
                                    <BringzzText tag='h4'>€ {parseFloat(orderDetails.fees.types.bringzzClickCollect).toFixed(2)}</BringzzText>
                                </div>
                            )}
                            {orderDetails.fees.types.bringzzMail && (
                                <div className='flex justify-between'>
                                    <BringzzText tag='h4' className='text-gray-600'>{t('component.order_view.mail_delivery_fee')}</BringzzText>
                                    <BringzzText tag='h4'>€ {parseFloat(orderDetails.fees.types.bringzzMail).toFixed(2)}</BringzzText>
                                </div>
                            )}
                            {orderDetails.fees.types.shopperFee && (
                                <div className='flex justify-between'>
                                    <BringzzText tag='h4' className='text-gray-600'>{t('component.order_view.shopper_fee')}</BringzzText>
                                    <BringzzText tag='h4'>€ {parseFloat(orderDetails.fees.types.shopperFee).toFixed(2)}</BringzzText>
                                </div>
                            )}
                            {orderDetails.fees.types.postFee && (
                                <div className='flex justify-between'>
                                    <BringzzText tag='h4' className='text-gray-600'>{t('component.order_view.post_fee')}</BringzzText>
                                    <BringzzText tag='h4'>€ {parseFloat(orderDetails.fees.types.postFee).toFixed(2)}</BringzzText>
                                </div>
                            )}
                            {orderDetails.fees.types.nightDutyFee && (
                                <div className='flex justify-between'>
                                    <BringzzText tag='h4' className='text-gray-600'>{t('component.order_view.night_duty_fee')}</BringzzText>
                                    <BringzzText tag='h4'>€ {parseFloat(orderDetails.fees.types.nightDutyFee).toFixed(2)}</BringzzText>
                                </div>
                            )}
                            {orderDetails.fees.types.sundayFee && (
                                <div className='flex justify-between'>
                                    <BringzzText tag='h4' className='text-gray-600'>{t('component.order_view.sunday_fee')}</BringzzText>
                                    <BringzzText tag='h4'>€ {parseFloat(orderDetails.fees.types.sundayFee).toFixed(2)}</BringzzText>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {USER_ROLES.SHOPPER_ROLE == user.userRole &&
                    <BringzzReadOnlyMap
                        startLocation={{ lat: 48.2082, lng: 16.3738 }} // Vienna
                        endLocation={{ lat: 48.1951, lng: 16.3457 }}   // Schönbrunn
                    />
                }

                <BringzzCustomerCardSmall customer={orderDetails.customer} />

                {user.userRole === USER_ROLES.SHOPPER_ROLE && (
                    <>
                        <div className="flex items-center justify-between p-3 border-b">
                            <div className="items-center flex gap-x-6">
                                <span>
                                    <BringzzText tag="h4" className="font-regular">
                                        {t('component.order_view.estimated_total_distance')}
                                    </BringzzText>
                                </span>
                            </div>
                            <span className="font-bold">
                                <BringzzText>9.9km</BringzzText>
                            </span>
                        </div>
                        <div className="flex items-center justify-between p-3 border-b">
                            <div className="items-center flex gap-x-6">
                                <span>
                                    <BringzzText tag="h4" className="font-regular">
                                        {t('component.order_view.estimated_time')}
                                    </BringzzText>
                                </span>
                            </div>
                            <span className="font-bold">20min</span>
                        </div>
                        <div className="flex items-center justify-between p-3 border-b">
                            <div className="items-center flex gap-x-6">
                                <span>
                                    <BringzzText tag="h4" className="font-regular">
                                        {t('component.order_view.estimated_points')}
                                    </BringzzText>
                                </span>
                            </div>
                            <span className="font-bold">XXpts</span>
                        </div>
                        <div className="flex items-center justify-between p-3 border-b">
                            <div className="items-center flex gap-x-6">
                                <span>
                                    <BringzzText tag="h4" className="font-regular">
                                        {t('component.order_view.estimated_pay')}
                                    </BringzzText>
                                </span>
                            </div>
                            <span className="font-bold">XX€</span>
                        </div>
                        <div className="flex items-center justify-between p-3 border-b">
                            <div className="items-center flex gap-x-6">
                                <span>
                                    <BringzzText tag="h4" className="font-regular">
                                        {t('component.order_view.estimated_tip')}
                                    </BringzzText>
                                </span>
                            </div>
                            <span className="font-bold">XX€</span>
                        </div>
                    </>
                )}

                {USER_ROLES.PHARMACY_ROLE == user.userRole &&
                    <>
                        <div className='flex space-x-4 border-b py-2'>
                            <div>
                                <BringzzIcon
                                    folder='LineIcons'
                                    icon='IconBox9'
                                    size='22'
                                    className='p-3 rounded-full' />
                            </div>
                            <div className='flex w-full'>
                                <div>
                                    <BringzzText tag='h3'>{t('component.order_view.order_type')}</BringzzText>
                                    <BringzzText tag='h4' className={"font-normal"}>{getDeliveryTypeLabel(orderDetails.type, t)}</BringzzText>
                                </div>
                            </div>
                        </div>
                        {(DELIVERY_TYPES.BRINGZZ_PICKUP == orderDetails.type || DELIVERY_TYPES.MAIL_DELIVERY == orderDetails.type) && <div className='flex space-x-4 border-b py-2'>
                            <div>
                                <BringzzIcon
                                    folder='LineIcons'
                                    icon='IconMapMarker'
                                    size='22'
                                    className='p-3 rounded-full' />
                            </div>
                            <div className='flex w-full justify-between'>
                                <div>
                                    <BringzzText tag='h3'>{t('component.order_view.delivering_to')}</BringzzText>
                                    <BringzzText tag='h4' className={"font-light"}>{t('component.order_view.home')}</BringzzText>
                                </div>
                            </div>
                        </div>}
                        <div className='flex space-x-4 border-b py-2'>
                            <div>
                                <BringzzIcon
                                    folder='LineIcons'
                                    icon='IconFile'
                                    size='22'
                                    className='p-3 rounded-full' />
                            </div>
                            <div className={`flex w-full justify-between ${!(orderDetails?.prescription && (Array.isArray(orderDetails.prescription) ? orderDetails.prescription.length > 0 : Object.keys(orderDetails.prescription).length > 0)) ? 'opacity-35' : ''}`}>
                                <div>
                                    <BringzzText tag='h3'>{t('component.order_view.prescription')}</BringzzText>
                                    <BringzzText tag='h4' className={"font-normal"}>
                                        {orderDetails?.prescription?.prescriptionId || t('component.order_view.not_provided')}
                                    </BringzzText>
                                </div>
                                <div className='flex items-center space-x-2'>
                                    <BringzzIcon
                                        folder='LineIcons'
                                        icon='IconEye'
                                        size='22'
                                        className={(orderDetails?.prescription && (Array.isArray(orderDetails.prescription) ? orderDetails.prescription.length > 0 : Object.keys(orderDetails.prescription).length > 0)) ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}
                                        onClick={() => (orderDetails?.prescription && (Array.isArray(orderDetails.prescription) ? orderDetails.prescription.length > 0 : Object.keys(orderDetails.prescription).length > 0)) && setShowPrescriptionModal(true)} />
                                    {/* <BringzzIcon
                                        folder='LineIcons'
                                        icon='IconBarcode'
                                        size='22' /> */}
                                </div>
                            </div>
                        </div>

                        <BringzzOrderCardSmallAccordion defaultExpanded={true} totalPrice={orderDetails.totalPrice} products={orderDetails.products} fees={orderDetails?.fees.totalFees || {}}/>
                    </>
                }
            </div>}

            {/* Prescription Image Viewer Modal */}
            <ImageViewerModal
                isOpen={showPrescriptionModal}
                onClose={() => setShowPrescriptionModal(false)}
                imageUrl={orderDetails?.prescription?.photo || null}
                title={t('component.order_view.prescription')}
            />

        </BringzzPageContainer >
    );
};

export default OrderViewPage;
